import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * HtmlRenderer - Renders HTML content (full document or raw fragment) in a sandboxed iframe.
 * 
 * Handles two cases from the backend:
 * 1. Full HTML documents (<!DOCTYPE html>...) - used for frontend_code after evaluation
 * 2. Raw HTML fragments (<div>...</div>) - used for highlight_chart and initial frontend_code
 *
 * Using srcdoc isolates chart canvas from React's render cycle so charts never
 * disappear on sidebar toggles, state changes, or re-renders.
 */
export const HtmlRenderer = ({
    htmlContent,
    className = '',
    minHeight = 150,
    /** Padding inside iframe `<body>` for fragment documents (px). Charts/highlights default 12. */
    bodyPaddingPx = 12,
}) => {
    const { t } = useTranslation('shared-common');
    const [height, setHeight] = useState(minHeight);
    const frameId = useRef(`htmlrenderer_${Math.random().toString(36).slice(2)}`);

    const paddedPx = Number.isFinite(Number(bodyPaddingPx))
        ? Math.min(96, Math.max(0, Math.round(Number(bodyPaddingPx))))
        : 12;

    const injectedContent = useMemo(() => {
        if (!htmlContent) return '';
        const id = frameId.current;

        // Chart.js guardrails (enforced at runtime in the iframe).
        // This protects the UI from occasional LLM prompt non-compliance in highlight_chart/frontend_code:
        // - Never allow pie charts (force doughnut)
        // - Never allow horizontal bars (force vertical by removing indexAxis:'y' and banning horizontalBar)
        const chartGuard = `
<script>
(function(){
  function sanitizeConfig(cfg){
    try {
      if (!cfg || typeof cfg !== 'object') return cfg;
      // Normalize chart type
      if (cfg.type === 'pie') cfg.type = 'doughnut';
      if (cfg.type === 'horizontalBar') cfg.type = 'bar';

      var opt = cfg.options || (cfg.options = {});
      // Force vertical bars
      if (opt && opt.indexAxis === 'y') {
        delete opt.indexAxis;
      }
      // Defensive: some configs nest indexAxis incorrectly or repeat it
      if (cfg.indexAxis === 'y') {
        delete cfg.indexAxis;
      }

      // Dataset-level type overrides (rare, but possible in mixed charts)
      var ds = (cfg.data && cfg.data.datasets) ? cfg.data.datasets : null;
      if (Array.isArray(ds)) {
        for (var i = 0; i < ds.length; i++) {
          if (ds[i] && ds[i].type === 'pie') ds[i].type = 'doughnut';
          if (ds[i] && ds[i].type === 'horizontalBar') ds[i].type = 'bar';
        }
      }
    } catch (e) {}
    return cfg;
  }

  function wrapChart(){
    try {
      if (!window.Chart || window.Chart.__guardWrapped) return;
      var Original = window.Chart;
      function GuardedChart(el, cfg){
        return new Original(el, sanitizeConfig(cfg));
      }
      // Copy static properties/plugins (Chart is a function with props)
      for (var k in Original) {
        try { GuardedChart[k] = Original[k]; } catch (e) {}
      }
      GuardedChart.__guardWrapped = true;
      window.Chart = GuardedChart;
    } catch (e) {}
  }

  // Chart.js defines window.Chart synchronously when the script loads; wrap immediately if present.
  wrapChart();
})();
</script>`;

        // Height reporter — uses postMessage so we don't need allow-same-origin
        const reporter = `
<script>
(function(){
  var frameId = '${id}';
  function sendHeight(){
    var b = document.body;
    var e = document.documentElement;
    if (!b || !e) return;
    var h = Math.max(
      b.scrollHeight, b.offsetHeight, b.clientHeight,
      e.scrollHeight, e.offsetHeight, e.clientHeight
    );
    window.parent.postMessage({ type: 'htmlRendererHeight', frameId: frameId, height: h }, '*');
  }
  function bindResizeObserver(){
    try {
      if (typeof ResizeObserver === 'undefined') return;
      var ro = new ResizeObserver(function(){ sendHeight(); });
      ro.observe(document.documentElement);
      if (document.body) ro.observe(document.body);
    } catch (err) {}
  }
  window.addEventListener('load', function(){
    sendHeight();
    setTimeout(sendHeight, 50);
    setTimeout(sendHeight, 300);
    setTimeout(sendHeight, 800);
    setTimeout(sendHeight, 2000);
    setTimeout(sendHeight, 3500);
  });
  document.addEventListener('DOMContentLoaded', function(){
    sendHeight();
    bindResizeObserver();
    var mo = new MutationObserver(sendHeight);
    if (document.body) mo.observe(document.body, { childList: true, subtree: true, attributes: true });
    Array.prototype.forEach.call(document.images || [], function(img){
      if (!img.complete) img.addEventListener('load', sendHeight);
    });
  });
})();
</script>`;

        const isFullDocument = htmlContent.trimStart().toLowerCase().startsWith('<!doctype') ||
            htmlContent.trimStart().toLowerCase().startsWith('<html');

        if (isFullDocument) {
            // Full document — inject reporter just before </body>
            if (htmlContent.includes('</body>')) {
                return htmlContent.replace('</body>', reporter + '</body>');
            }
            return htmlContent + reporter;
        }

        // Raw fragment — wrap it in a full document with Tailwind + Chart.js pre-loaded
        // This fixes: missing Tailwind classes causing oversized icons, Chart.js not available
        return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
${chartGuard}
<script src="https://cdn.tailwindcss.com"></script>
<style>
  * { box-sizing: border-box; }
  html {
    overflow-x: hidden;
    overflow-y: visible !important;
    height: auto !important;
  }
  body {
    margin: 0;
    padding: ${paddedPx}px;
    font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
    background: transparent;
    overflow-x: hidden;
    overflow-y: visible !important;
    height: auto !important;
    max-height: none !important;
  }
  img { max-width: 100%; height: auto; }
  canvas { display: block; }
</style>
</head>
<body>
${htmlContent}
${reporter}
</body>
</html>`;
    }, [htmlContent, paddedPx]);

    useEffect(() => {
        const id = frameId.current;
        const handler = (event) => {
            if (
                event.data &&
                event.data.type === 'htmlRendererHeight' &&
                event.data.frameId === id
            ) {
                const newHeight = Math.max(Number(event.data.height) + 24, minHeight);
                setHeight(newHeight);
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [minHeight]);

    if (!htmlContent) return null;

    const trimmedFrameClass = (className || '').trim();
    const frameClassName = trimmedFrameClass
        ? ['w-full', 'block', trimmedFrameClass].join(' ')
        : 'w-full border-none block';

    return (
        <iframe
            srcDoc={injectedContent}
            title={t('htmlRenderer.title')}
            className={frameClassName}
            style={{ height: `${height}px`, overflow: 'hidden', display: 'block' }}
            sandbox="allow-scripts"
        />
    );
};

export default HtmlRenderer;